import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw, Loader2, User, Phone, Mail, Copy, ExternalLink,
  ChevronDown, Check, History, Wallet, Home, Download, Gamepad2
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Дашборд 1Win' },
  { id: 'history', name: 'История', icon: History, description: 'Операции и ставки' },
  { id: 'wallet', name: 'Касса', icon: Wallet, description: 'Пополнение / Вывод' },
  { id: 'profile', name: 'Профиль', icon: User, description: 'Данные пользователя' },
];

export default function OneWinTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedPage, setSelectedPage] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('dark');
  const [addRandomOps, setAddRandomOps] = useState(true);
  const [bonusBalance, setBonusBalance] = useState('0');
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      const profilesWithIds = data.map(p => ({
        ...p,
        winId: p.winId || Math.floor(100000 + Math.random() * 900000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
      setProfiles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const parseD = (d, t) => {
    if (!d) return 0;
    const p = d.split('.');
    if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}T${t || '00:00'}`).getTime();
    return 0;
  };

  const fmtDate = (ts) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  };

  const generateTransactions = (transactions) => {
    const result = [];
    
    // INVERT: OZON deposit → 1Win withdrawal (вывод), OZON withdrawal → 1Win deposit (пополнение)
    const sorted = [...(transactions || [])].sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));

    for (const tx of sorted) {
      const isOzonDeposit = tx.type === 'deposit';
      result.push({
        date: tx.date,
        time: tx.time || `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
        type: isOzonDeposit ? 'withdrawal' : 'deposit',
        amount: Math.abs(parseFloat(tx.amount || 0)),
      });
    }

    if (addRandomOps && result.length > 0) {
      const dates = result.map(b => parseD(b.date, b.time));
      const minT = Math.min(...dates);
      const maxT = Math.max(...dates);
      const count = Math.floor(Math.random() * 4) + 3; // 3-6 random ops

      for (let i = 0; i < count; i++) {
        const t = new Date(minT + Math.random() * (maxT - minT));
        const isDeposit = Math.random() > 0.45;
        result.push({
          date: fmtDate(t.getTime()),
          time: `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
          type: isDeposit ? 'deposit' : 'withdrawal',
          amount: Math.floor(Math.random() * 12000) + 500,
        });
      }

      // First deposit (oldest) — always пополнение up to 6000 RUB
      const oldestDate = new Date(minT);
      oldestDate.setDate(oldestDate.getDate() - Math.floor(Math.random() * 5) - 2); // 2-6 days before oldest
      const fdAmount = Math.floor(Math.random() * 4000) + 2000; // 2000-6000
      result.push({
        date: fmtDate(oldestDate.getTime()),
        time: `${String(Math.floor(Math.random()*12)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
        type: 'deposit',
        amount: fdAmount,
        isFirstDeposit: true,
      });
    }

    // Sort by date desc
    result.sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));
    return result;
  };

  const handleGenerate = () => {
    if (!selectedProfile) { toast.error("Выберите профиль"); return; }
    setIsGenerating(true);

    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}@mail.ru`;
    const balance = selectedProfile.template_balance || selectedProfile.balance || 0;
    const txs = generateTransactions(selectedProfile.transactions || []);

    const data = {
      firstName, lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone), email, balance,
      bonusBalance: parseFloat(bonusBalance) || 0,
      visibleId: selectedProfile.winId,
      transactions: txs,
      showBrowser, browserTheme,
    };

    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = (page) => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    return `${window.location.origin}/templates/1win/combined?data=${dataStr}&page=${page || selectedPage}&frame=${showBrowser}`;
  };

  const openPreview = () => { const url = getPreviewUrl(); if (url) window.open(url, '_blank'); };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) { navigator.clipboard.writeText(url); toast.success("Ссылка скопирована!"); }
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов всех страниц...");
    try {
      const response = await axios.post(
        `${API}/templates/1win/archive`,
        { data: generatedData, pages: ['main', 'history', 'wallet', 'profile'] },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `1Win_${generatedData.visibleId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="onewin-template-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center shadow-lg">
              <Gamepad2 className="w-5 h-5 text-white" />
            </div>
            1Win
          </h1>
          <p className="text-slate-500 mt-1">Генератор страницы казино 1Win</p>
        </div>
        <Button onClick={() => fetchProfiles()} variant="outline" data-testid="refresh-profiles-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>

            {/* Page Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Страница</Label>
              <div className="grid grid-cols-4 gap-2">
                {PAGES.map((page) => {
                  const Icon = page.icon;
                  return (
                    <button key={page.id} onClick={() => setSelectedPage(page.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedPage === page.id ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}>
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${selectedPage === page.id ? 'text-green-500' : 'text-slate-400'}`} />
                      <div className={`text-xs font-medium ${selectedPage === page.id ? 'text-green-700' : 'text-slate-600'}`}>{page.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Browser Frame */}
            <div className="mb-4">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-green-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={showBrowser} onChange={(e) => { setShowBrowser(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-green-500 focus:ring-green-500" />
                <div>
                  <div className="font-medium text-slate-700">Рамка браузера</div>
                  <div className="text-xs text-slate-500">Chrome-рамка с вкладками и адресной строкой</div>
                </div>
              </label>
            </div>

            {/* Browser Theme */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="text-sm font-medium text-slate-700 mb-2">Фон браузера</div>
              <div className="flex gap-3">
                <button onClick={() => { setBrowserTheme('light'); setGeneratedData(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    browserTheme === 'light' ? 'border-green-500 bg-white text-slate-800' : 'border-slate-200 bg-white text-slate-500'
                  }`}>Светлый</button>
                <button onClick={() => { setBrowserTheme('dark'); setGeneratedData(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    browserTheme === 'dark' ? 'border-green-500 bg-slate-800 text-white' : 'border-slate-200 bg-slate-700 text-slate-300'
                  }`}>Тёмный</button>
              </div>
            </div>

            {/* Random Operations */}
            <div className="mb-4">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-green-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={addRandomOps} onChange={(e) => { setAddRandomOps(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-green-500 focus:ring-green-500" />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">3-6 случайных ввод/вывод + первое пополнение до 6000 р.</div>
                </div>
              </label>
            </div>

            {/* Bonus Balance */}
            <div className="mb-6">
              <Label className="text-slate-700">Бонусный баланс (руб.)</Label>
              <Input type="number" value={bonusBalance}
                onChange={(e) => { setBonusBalance(e.target.value); setGeneratedData(null); }}
                placeholder="0" className="mt-1" />
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button data-testid="profile-dropdown" onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors">
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.winId} | {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} р.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">Нет профилей</div>
                    ) : (
                      profiles.map((profile) => (
                        <button key={profile.id}
                          onClick={() => { setSelectedProfile(profile); setDropdownOpen(false); setGeneratedData(null); }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 ${selectedProfile?.id === profile.id ? 'bg-green-50' : ''}`}>
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.name}</div>
                            <div className="text-xs text-slate-500">{(profile.template_balance || profile.balance || 0).toLocaleString()} р. | {(profile.transactions || []).length} транзакций</div>
                          </div>
                          {selectedProfile?.id === profile.id && <Check className="w-4 h-4 text-green-500" />}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <Button data-testid="generate-btn" onClick={handleGenerate} disabled={!selectedProfile || isGenerating}
              className="w-full bg-green-600 hover:bg-green-700 text-white gap-2">
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />}
              Сформировать страницу
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
            {generatedData ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-800">{generatedData.fullName}</div>
                    <div className="text-green-600 font-medium">{parseFloat(generatedData.balance).toLocaleString()} р.</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-600"><Phone className="w-4 h-4 text-slate-400" /><span>{generatedData.phone}</span></div>
                  <div className="flex items-center gap-3 text-slate-600"><Mail className="w-4 h-4 text-slate-400" /><span>{generatedData.email}</span></div>
                  <div className="flex items-center gap-3 text-slate-600"><History className="w-4 h-4 text-slate-400" /><span>{generatedData.transactions.length} операций</span></div>
                  {generatedData.bonusBalance > 0 && (
                    <div className="flex items-center gap-3 text-slate-600"><Wallet className="w-4 h-4 text-slate-400" /><span>Бонус: {parseFloat(generatedData.bonusBalance).toLocaleString()} р.</span></div>
                  )}
                </div>
                <div className="flex gap-3 pt-4">
                  <Button data-testid="open-preview-btn" onClick={openPreview} className="flex-1 bg-green-600 hover:bg-green-700 gap-2">
                    <ExternalLink className="w-4 h-4" /> Открыть
                  </Button>
                  <Button data-testid="copy-link-btn" variant="outline" onClick={copyLink} className="gap-2">
                    <Copy className="w-4 h-4" /> Копировать
                  </Button>
                </div>
                <Button onClick={downloadArchive} disabled={isDownloadingArchive} variant="outline" className="w-full mt-3" data-testid="download-archive-btn">
                  {isDownloadingArchive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Скачать архив всех страниц
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Gamepad2 className="w-16 h-16 text-slate-200 mb-4" />
                <div className="text-slate-400">Выберите профиль и нажмите<br />"Сформировать страницу"</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
